import { computed, ref } from "vue";
import { uuid } from "vue-uuid";
import { filesService } from "@/services/filesService";
import { DEFAULT_MAX_SIZE_BYTES, MAX_CHART_FILE_SIZE_BYTES, isPdfFile, parseUploadError, uploadService, validateFile, validatePdf, } from "@/services/uploadService";
import { tryParseApiErrors } from "@/utils";
/**
 * One upload path for chart documents: every file, PDF or scanner image, goes browser → S3, then the
 * prepare job converts images and compresses anything over the CMM budget. The per-file steps are
 * exported for uploaders that keep their own rows (the PA page's supporting-document slots); the
 * composable owns the import page's rows plus their picker / drag-and-drop handlers.
 */
// CoverMyMeds' attachment total per request; the backend clamps a prepare job to the same figure.
export const CMM_ATTACHMENT_BUDGET_BYTES = 10 * 1024 * 1024;
const PREPARE_POLL_INTERVAL_MS = 1000;
// Each matches a server bound: a job waits in the queue at most until it expires, then runs at most to its hard
// time limit. The poll counts the second window from the first "processing", not from the request.
const PREPARE_QUEUE_TIMEOUT_MS = 10 * 60 * 1000;
const PREPARE_RUN_TIMEOUT_MS = 10 * 60 * 1000;
const PDF_BROWSER_CHECK_MAX_BYTES = DEFAULT_MAX_SIZE_BYTES;
const PREPARE_POLL_MAX_CONSECUTIVE_FAILURES = 3;
const PREPARE_TIMEOUT_MESSAGE = "Compression is taking longer than expected. Please try again.";
const ROW_OCCUPIED_MESSAGE = "This document already has a file. Remove it first to upload a new one.";
const FREE_BUDGET_HINT = "Removing another document frees budget for this one.";
const MB = 1024 * 1024;
export const emptyChartDoc = () => ({
    label: null,
    links: [],
    file: null,
    sizeBytes: null,
    pageCount: null,
    droppedPages: [],
    uploading: false,
    preparing: false,
    error: null,
    errorDetails: null,
});
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const awaitPrepared = async (started) => {
    if (started.status !== "processing")
        return started;
    let deadline = Date.now() + PREPARE_QUEUE_TIMEOUT_MS;
    let running = false;
    let consecutiveFailures = 0;
    while (Date.now() < deadline) {
        await sleep(PREPARE_POLL_INTERVAL_MS);
        let result;
        try {
            result = await filesService.getPrepareDocumentStatus(started.taskId);
        }
        catch (pollFailure) {
            if (++consecutiveFailures >= PREPARE_POLL_MAX_CONSECUTIVE_FAILURES)
                throw pollFailure;
            continue;
        }
        consecutiveFailures = 0;
        if (result.status !== "queued" && result.status !== "processing")
            return result;
        if (result.status === "processing" && !running) {
            running = true;
            deadline = Date.now() + PREPARE_RUN_TIMEOUT_MS;
        }
    }
    return { status: "error", error: PREPARE_TIMEOUT_MESSAGE };
};
const toOutcome = (prepared, budgetBytes) => {
    if (prepared.status === "error") {
        return {
            status: "error",
            message: prepared.error,
            errorDetails: {
                achievedSize: prepared.achievedSize,
                pageCount: prepared.pageCount,
                pagesThatFit: prepared.pagesThatFit,
                floorBytesPerPage: prepared.floorBytesPerPage,
                budgetBytes,
                perPageBytes: prepared.perPageBytes,
                bestUploadKey: prepared.bestUploadKey,
                bestDownloadUrl: prepared.bestDownloadUrl,
            },
        };
    }
    return {
        status: "done",
        downloadUrl: prepared.downloadUrl,
        uploadKey: prepared.uploadKey,
        sizeBytes: prepared.compressedSize,
        pageCount: prepared.pageCount ?? null,
        droppedPages: prepared.droppedPages ?? [],
        pageRanges: prepared.pageRanges,
    };
};
const runPrepare = async (started, budget) => toOutcome(await awaitPrepared(started), started.budgetBytes ?? budget.budgetBytes ?? CMM_ATTACHMENT_BUDGET_BYTES);
/** Validates and sends one chart file browser → S3. Throws with a user-facing message. */
export const uploadChartFile = async (file) => {
    const validationError = validateFile(file, { maxSizeBytes: MAX_CHART_FILE_SIZE_BYTES });
    if (validationError)
        throw new Error(validationError);
    if (isPdfFile(file, file.name) && file.size <= PDF_BROWSER_CHECK_MAX_BYTES)
        await validatePdf(file);
    return uploadService.uploadDirect(file, `${uuid.v4()}/${file.name}`, { chart: true });
};
/**
 * Runs the prepare job for an uploaded chart file. Every file goes through it, a PDF that already fits
 * included: the job is what moves the object out of the chart prefix into the attachable one, and a link
 * still under the chart prefix is refused at submit as an unprepared upload. A file needing no conversion
 * costs nothing extra — the server answers it inline.
 */
export const prepareUploadedChart = async (uploaded, budget) => runPrepare(await filesService.prepareDocument(uploaded.uploadKey, budget), budget);
/** The picker's confirm: a PDF from ``pages`` of the kept best attempt, compressed to the remaining budget. */
export const subsetChart = async (bestUploadKey, pages, budget) => runPrepare(await filesService.subsetPdf(bestUploadKey, pages, budget), budget);
export const failureMessage = (failure, hasOtherDocs) => hasOtherDocs ? `${failure.message} ${FREE_BUDGET_HINT}` : failure.message;
export const canChoosePagesFrom = (errorDetails) => Boolean(errorDetails?.bestDownloadUrl && errorDetails?.perPageBytes?.length);
/** "pages 1-30 · 0.8 MB · 10 blank pages removed." — what the prepare job did to a document. */
export const preparedCaption = (prepared) => {
    const parts = [];
    if (prepared.pageRanges)
        parts.push(`pages ${prepared.pageRanges}`);
    parts.push(`${(prepared.sizeBytes / MB).toFixed(prepared.sizeBytes < MB ? 2 : 1)} MB`);
    const dropped = prepared.droppedPages.length;
    if (dropped)
        parts.push(`${dropped} blank ${dropped === 1 ? "page" : "pages"} removed`);
    return parts.join(" · ");
};
export function useChartUpload() {
    const docs = ref([emptyChartDoc()]);
    const fileInputRefs = ref({});
    const dragOverIndex = ref(null);
    let dragEnterCount = 0;
    const addDoc = () => docs.value.push(emptyChartDoc());
    const removeDoc = (index) => docs.value.splice(index, 1);
    const resetDocs = () => {
        docs.value = [emptyChartDoc()];
        dragOverIndex.value = null;
        dragEnterCount = 0;
    };
    const hasUploadedDocs = () => docs.value.some((doc) => doc.links.length > 0);
    const isAnyUploadInProgress = () => docs.value.some((doc) => doc.uploading || doc.preparing);
    const documentLinks = () => docs.value.flatMap((doc) => doc.links);
    const otherFinalisedDocs = (doc) => docs.value.filter((other) => other !== doc && other.links.length > 0);
    const canChoosePages = (doc) => canChoosePagesFrom(doc.errorDetails);
    const remainingBudget = (doc) => Math.max(0, CMM_ATTACHMENT_BUDGET_BYTES - otherFinalisedDocs(doc).reduce((sum, other) => sum + (other.sizeBytes ?? 0), 0));
    const stillListed = (doc) => docs.value.includes(doc);
    let prepareQueue = Promise.resolve();
    const queuePrepare = (job) => {
        const queued = prepareQueue.then(job, job);
        prepareQueue = queued.catch(() => undefined);
        return queued;
    };
    const onDragEnter = (index) => {
        dragEnterCount++;
        dragOverIndex.value = index;
    };
    const onDragLeave = () => {
        dragEnterCount--;
        if (dragEnterCount <= 0) {
            dragEnterCount = 0;
            dragOverIndex.value = null;
        }
    };
    const failWith = (doc, failure) => {
        doc.error = failureMessage(failure, otherFinalisedDocs(doc).length > 0);
        doc.errorDetails = failure.errorDetails;
    };
    const finalise = (doc, label, prepared) => {
        doc.error = null;
        doc.errorDetails = null;
        doc.links = [prepared.downloadUrl];
        doc.label = label;
        doc.sizeBytes = prepared.sizeBytes;
        doc.pageCount = prepared.pageCount;
        doc.droppedPages = prepared.droppedPages;
    };
    const processFile = async (index, file) => {
        const doc = docs.value[index];
        doc.error = null;
        doc.errorDetails = null;
        if (!file)
            return;
        doc.uploading = true;
        let uploaded;
        try {
            uploaded = await uploadChartFile(file);
        }
        catch (error) {
            doc.error = parseUploadError(error);
            return;
        }
        finally {
            doc.uploading = false;
        }
        // links stay unset until the document is final, so a preparing doc still reads as pending.
        doc.file = file;
        doc.preparing = true;
        await queuePrepare(async () => {
            try {
                const outcome = await prepareUploadedChart(uploaded, { budgetBytes: remainingBudget(doc) });
                if (!stillListed(doc))
                    return;
                if (outcome.status === "error")
                    failWith(doc, outcome);
                else
                    finalise(doc, file.name, outcome);
            }
            catch (error) {
                doc.error = tryParseApiErrors(error)[0];
            }
            finally {
                doc.preparing = false;
            }
        });
    };
    const pickerIndex = ref(null);
    const pickerDoc = computed(() => (pickerIndex.value === null ? null : docs.value[pickerIndex.value]));
    const openPicker = (index) => {
        pickerIndex.value = index;
    };
    const closePicker = () => {
        pickerIndex.value = null;
    };
    const chooseSubset = async (index, pages) => {
        const doc = docs.value[index];
        const bestUploadKey = doc.errorDetails?.bestUploadKey;
        if (!bestUploadKey)
            return;
        const name = doc.file?.name ?? "document";
        doc.error = null;
        doc.preparing = true;
        await queuePrepare(async () => {
            try {
                const outcome = await subsetChart(bestUploadKey, pages, { budgetBytes: remainingBudget(doc) });
                if (!stillListed(doc))
                    return;
                if (outcome.status === "error")
                    failWith(doc, outcome);
                else
                    finalise(doc, outcome.pageRanges ? `${name} (pages ${outcome.pageRanges})` : name, outcome);
            }
            catch (error) {
                doc.error = tryParseApiErrors(error)[0];
            }
            finally {
                doc.preparing = false;
            }
        });
    };
    const confirmPicker = async (pages) => {
        const index = pickerIndex.value;
        closePicker();
        if (index !== null)
            await chooseSubset(index, pages);
    };
    const acceptsFile = (doc) => {
        if (doc.links.length) {
            doc.error = ROW_OCCUPIED_MESSAGE;
            return false;
        }
        return !doc.uploading && !doc.preparing;
    };
    const handleFileDrop = async (index, event) => {
        dragOverIndex.value = null;
        dragEnterCount = 0;
        if (!acceptsFile(docs.value[index]))
            return;
        const file = event.dataTransfer?.files?.[0];
        if (file)
            await processFile(index, file);
    };
    const handleFileSelect = async (index, event) => {
        const input = event.target;
        const file = input.files?.[0];
        input.value = "";
        if (acceptsFile(docs.value[index]))
            await processFile(index, file);
    };
    return {
        docs,
        fileInputRefs,
        dragOverIndex,
        addDoc,
        removeDoc,
        resetDocs,
        hasUploadedDocs,
        isAnyUploadInProgress,
        documentLinks,
        remainingBudget,
        canChoosePages,
        pickerDoc,
        openPicker,
        closePicker,
        confirmPicker,
        chooseSubset,
        onDragEnter,
        onDragLeave,
        handleFileDrop,
        handleFileSelect,
        processFile,
    };
}
